/**
 * Dummy data constants for Chat Screen
 */

export interface ModeChip {
  id: string;
  label: string;
  icon?: string;
}

export interface TabItem {
  id: string;
  label: string;
}

export interface ActivityCard {
  id: string;
  title: string;
  image: string;
  location?: string;
  rating?: number;
}

export interface PlaceOfRelevance {
  id: string;
  title: string;
  cards: ActivityCard[];
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

export interface PeopleAlsoAsk {
  id: string;
  question: string;
}

export interface TravelMode {
  id: string;
  label: string;
  icon: string;
  recommended?: boolean;
  fare?: string;
  description?: string;
}

export interface TerrainType {
  id: string;
  title: string;
  image: string;
  description: string;
}

export interface LocationHistory {
  id: string;
  title: string;
  image: string;
  description: string;
}

export interface FoodSpecialty {
  id: string;
  name: string;
  image: string;
  description: string;
}

export interface LocalExperience {
  id: string;
  title: string;
  image: string;
  description: string;
  price?: string;
}

export interface ExperienceCard {
  id: string;
  title: string;
  image: string;
  images?: string[]; // Optional array of all images for carousel
  location: string;
  type: 'activities' | 'package' | 'riders-corner' | 'signature' | 'stays';
  slug?: string; // Optional slug for navigation
  // Optional fields from API
  rating?: number;
  ratingCount?: number;
  description?: string;
  price?: string;
  currencySymbol?: string; // Currency symbol (e.g., '₹', '$')
  duration?: string | { days?: number; nights?: number; hours?: number };
  attractionsCount?: number;
  days?: number;
  nights?: number;
  locationsCount?: number;
  tags?: string[]; // Category tags like ['Adventure', 'Festival', 'Spiritual', 'Music', 'Yoga']
  // Optional coordinates for map display
  latitude?: number;
  longitude?: number;
}

// Mode Chips - Empty array, will be replaced by intent and mood from API
export const MODE_CHIPS: ModeChip[] = [];

// Tab Items
export const TAB_ITEMS: TabItem[] = [
  { id: 'discover', label: 'Discover' },
  { id: 'places', label: 'Places' },
  { id: 'experiences', label: 'Experiences' },
  { id: 'map', label: 'Map' },
];

// Overview Data
export const OVERVIEW_DATA = {
  title: 'Dharamshala Overview',
  description:
    'Dharamshala is a beautiful hill station in Himachal Pradesh, India, known for its stunning mountain views, Tibetan culture, and spiritual significance. It serves as the headquarters of the Central Tibetan Administration and is home to the Dalai Lama.',
};

// Places of Relevance
export const PLACES_OF_RELEVANCE: PlaceOfRelevance[] = [
  {
    id: 'attractions',
    title: 'Places of Relevance',
    cards: [
      {
        id: '1',
        title: 'McLeod Ganj',
        image:
          'https://images.unsplash.com/photo-1511497584788-876760111969?w=400&h=300&fit=crop&q=80',
        location: 'Dharamshala',
        rating: 4.5,
      },
      {
        id: '2',
        title: 'Triund Hill',
        image:
          'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=300&fit=crop&q=80',
        location: 'Dharamshala',
        rating: 4.8,
      },
      {
        id: '3',
        title: 'Namgyal Monastery',
        image:
          'https://images.unsplash.com/photo-1511497584788-876760111969?w=400&h=300&fit=crop&q=80',
        location: 'Dharamshala',
        rating: 4.6,
      },
      {
        id: '4',
        title: 'Bhagsu Waterfall',
        image:
          'https://images.unsplash.com/photo-1547036967-23d11aacaee0?w=400&h=300&fit=crop&q=80',
        location: 'Dharamshala',
        rating: 4.4,
      },
      {
        id: '5',
        title: 'Dal Lake',
        image:
          'https://images.unsplash.com/photo-1501785888041-af3ef285b470?w=400&h=300&fit=crop&q=80',
        location: 'Dharamshala',
        rating: 4.3,
      },
      {
        id: '6',
        title: 'Kangra Fort',
        image:
          'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=300&fit=crop&q=80',
        location: 'Dharamshala',
        rating: 4.7,
      },
    ],
  },
];

// Chat Messages
export const CHAT_MESSAGES: ChatMessage[] = [
  {
    id: '1',
    role: 'user',
    content: 'Tell me about Dharamshala',
    timestamp: new Date('2024-01-15T10:00:00'),
  },
  {
    id: '2',
    role: 'assistant',
    content: `Dharamshala is a beautiful hill station in Himachal Pradesh, India. It is known for its stunning mountain views, Tibetan culture, and spiritual significance.

![Dharamshala Mountains](https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600&h=400&fit=crop&q=80)

The town is divided into two parts: Lower Dharamshala and Upper Dharamshala ([McLeod Ganj](https://example.com/mcleod-ganj)). McLeod Ganj is the home of the Dalai Lama and the Tibetan government-in-exile.

![Tibetan Monastery](https://images.unsplash.com/photo-1511497584788-876760111969?w=600&h=400&fit=crop&q=80)

**Key Highlights:**
- **Spiritual Center**: Home to the Dalai Lama and numerous Tibetan monasteries
- **Natural Beauty**: Surrounded by the Dhauladhar mountain range
- **Cultural Richness**: Blend of Indian and Tibetan cultures
- **Adventure Activities**: Trekking, paragliding, and nature walks

For more information, visit [Dharamshala Tourism](https://example.com/dharamshala-tourism).`,
    timestamp: new Date('2024-01-15T10:00:05'),
  },
];

// Travel Mode Description (single description for all modes)
// NOTE: This constant is currently UNUSED. Travel mode descriptions are now provided by the API.
// TODO: Remove if confirmed unused across the codebase
export const TRAVEL_MODE_DESCRIPTION =
  'Dharamshala is well-connected by road, rail, and air. The nearest airport is Gaggal Airport (DHM), about 15 km away. The nearest major railway station is Pathankot, about 85 km away. Buses and taxis are readily available for local and inter-state travel.';

// Terrain Types
export const TERRAIN_TYPES: TerrainType[] = [
  {
    id: 'mountain',
    title: 'Mountain Terrain',
    image:
      'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=500&fit=crop&q=80',
    description:
      'Steep slopes and high altitude areas perfect for trekking and mountaineering.',
  },
  {
    id: 'valley',
    title: 'Valley Terrain',
    image:
      'https://images.unsplash.com/photo-1501785888041-af3ef285b470?w=400&h=500&fit=crop&q=80',
    description: 'Beautiful valleys with lush greenery and flowing rivers.',
  },
  {
    id: 'forest',
    title: 'Forest Terrain',
    image:
      'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=400&h=500&fit=crop&q=80',
    description:
      'Dense forests with diverse flora and fauna, ideal for nature walks.',
  },
];

// Location History
export const LOCATION_HISTORY: LocationHistory[] = [
  {
    id: '1',
    title: 'Prominence and Dynasties',
    image:
      'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=300&h=300&fit=crop&q=80',
    description:
      'Dharamshala has a rich history dating back to ancient times, with various dynasties ruling the region and contributing to its cultural heritage.',
  },
  {
    id: '2',
    title: 'Tibetan Influence',
    image:
      'https://images.unsplash.com/photo-1511497584788-876760111969?w=300&h=300&fit=crop&q=80',
    description:
      'The region became a significant center for Tibetan culture and Buddhism after the Dalai Lama established his residence here in 1960.',
  },
  {
    id: '3',
    title: 'Singing Bowls',
    image:
      'https://images.unsplash.com/photo-1513475382585-d06e58bcb0e0?w=300&h=300&fit=crop&q=80',
    description:
      'Metal bowls that produce resonant tones when struck or rubbed. Used for meditation and sound healing.',
  },
  {
    id: '4',
    title: 'Prayer Wheels',
    image:
      'https://images.unsplash.com/photo-1511497584788-876760111969?w=300&h=300&fit=crop&q=80',
    description:
      'Cylindrical wheels inscribed with mantras. Spinning the wheel is believed to accumulate merit.',
  },
];

// Food Specialties
export const FOOD_SPECIALTIES: FoodSpecialty[] = [
  {
    id: '1',
    name: 'Momos',
    image:
      'https://images.unsplash.com/photo-1569718212165-3a8278d5f624?w=400&h=500&fit=crop&q=80',
    description:
      'Steamed or fried dumplings filled with meat, vegetables, or cheese. A staple food throughout the Himalayas.',
  },
  {
    id: '2',
    name: 'Thukpa',
    image:
      'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400&h=500&fit=crop&q=80',
    description:
      'A hearty noodle soup with meat or vegetables. Perfect for warming up in cold weather.',
  },
  {
    id: '3',
    name: 'Dal Bhat',
    image:
      'https://images.unsplash.com/photo-1512058564366-18510be2db19?w=400&h=500&fit=crop&q=80',
    description:
      'A traditional Nepali meal consisting of lentil soup (dal), rice (bhat), vegetables, and often meat or pickle.',
  },
  {
    id: '4',
    name: 'Tsampa',
    image:
      'https://images.unsplash.com/photo-1574484284002-952d92456975?w=400&h=500&fit=crop&q=80',
    description:
      'Roasted barley flour, a staple food in Tibetan regions. Often mixed with tea and butter.',
  },
];

// Local Experiences
export const LOCAL_EXPERIENCES: LocalExperience[] = [
  {
    id: '1',
    title: 'Trekking',
    image:
      'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=300&h=400&fit=crop&q=80',
    description:
      'Explore the beautiful Himalayan trails with experienced guides.',
    price: '₹3,000',
  },
  {
    id: '2',
    title: 'Monastery Visits',
    image:
      'https://images.unsplash.com/photo-1511497584788-876760111969?w=300&h=400&fit=crop&q=80',
    description: 'Experience the spiritual atmosphere of Tibetan monasteries.',
    price: 'Free',
  },
];

// Place Details
export const PLACE_DETAILS = {
  name: 'Dharamshala',
  images: [
    'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600&h=800&fit=crop&q=80',
    'https://images.unsplash.com/photo-1511497584788-876760111969?w=600&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1501785888041-af3ef285b470?w=600&h=400&fit=crop&q=80',
  ],
  bestTimeToVisit:
    'The best time to visit Dharamshala is generally during the spring (March to June) and autumn (September to November) seasons. These periods offer pleasant weather, clear skies, and comfortable temperatures for sightseeing and outdoor activities.',
  accessibility: {
    title: 'Who can Travel?',
    items: [
      {
        id: '1',
        heading: 'All Age Groups',
        description:
          'Dharamshala is suitable for travelers of all ages. However, some trekking routes may require physical fitness.',
        expanded: true,
      },
      {
        id: '2',
        heading: 'Family Friendly',
        description:
          'The town offers many family-friendly activities and accommodations.',
        expanded: false,
      },
      {
        id: '3',
        heading: 'Solo Travelers',
        description:
          'Safe and welcoming for solo travelers with good infrastructure.',
        expanded: false,
      },
    ],
  },
  navigationDifficulty: {
    title: 'Navigation Difficulty',
    items: [
      'Variable Difficulty: Navigation difficulty ranges from well-marked trails suitable for beginners to challenging off-trail routes requiring advanced mountaineering skills.',
      'Weather Dependence: Weather conditions can change rapidly in the mountains, impacting visibility and trail conditions. Snow, fog, and rain can make navigation significantly more challenging.',
    ],
  },
  culturalAspects: {
    title: 'Cultural & Social Aspects',
    image:
      'https://images.unsplash.com/photo-1511497584788-876760111969?w=600&h=400&fit=crop&q=80',
    items: [
      'Diversity: The Himalayas are home to a diverse range of ethnic groups, each with its own distinct language, customs, and traditions. Examples include Sherpas, Tibetans, Gurungs, and Lepchas.',
      'Festivals: The region celebrates numerous festivals throughout the year, including Losar (Tibetan New Year), Hemis Festival, and various local harvest festivals.',
      'Handicrafts and Weaving: Traditional crafts like Pashmina weaving, Thangka painting, and metalwork are important cultural practices passed down through generations.',
    ],
  },
  localLanguages: {
    title: 'Local Languages',
    items: [
      'Linguistic Diversity: The Himalayas are a region of immense linguistic diversity. Numerous languages and dialects are spoken, reflecting the diverse ethnic groups inhabiting the region.',
      'Tibetan Languages: Various Tibetan languages and dialects are spoken in the Tibetan regions of the Himalayas, including Tibet, Nepal, and Bhutan.',
      'Nepali: Nepali is the official language of Nepal and is widely spoken in the Nepali Himalayas.',
      'Other Regional Languages: Other languages spoken in the Himalayas include Kumaoni, Garhwali, Ladakhi, and various tribal languages.',
    ],
  },
  activities: {
    title: 'Activities and Attractions',
    items: [
      { icon: 'plane', label: 'Guided City Tour' },
      { icon: 'train', label: 'Nature Walks' },
      { icon: 'bus', label: 'Paragliding' },
      { icon: 'trekking', label: 'Yoga & Meditation' },
      { icon: 'plane', label: 'Cooking Classes' },
      { icon: 'train', label: 'Local Markets' },
      { icon: 'bus', label: 'Volunteer Programs' },
      { icon: 'trekking', label: 'Cultural Shows' },
      { icon: 'plane', label: 'Photography Tours' },
      { icon: 'train', label: 'Spa & Wellness' },
    ],
  },
  historicalSignificance: {
    title: 'Historical Significance',
    image:
      'https://images.unsplash.com/photo-1564507592333-c60657eea523?w=400&h=300&fit=crop&q=80',
    items: [
      'Ancient Trade Routes: The Himalayas have served as important trade routes connecting South Asia with Central Asia and East Asia for centuries. The Silk Road passed through several Himalayan passes.',
      'Religious Significance: The Himalayas are sacred to several religions, including Hinduism and Buddhism. Many pilgrimage sites and monasteries are located in the region.',
      'British Colonial Influence: During the British Raj, the Himalayas were used as summer retreats to escape the heat of the plains. Hill stations like Shimla and Darjeeling were established.',
    ],
  },
  amenitiesAndFacilities: {
    title: 'Amenities & Facilities',
    items: [
      {
        id: 'cleanliness',
        label: 'Cleanliness',
        icon: 'clean-hands',
      },
      {
        id: 'drinking-water',
        label: 'Drinking Water Availability',
        icon: 'water',
      },
      {
        id: 'toilet',
        label: 'Toilet Facility',
        icon: 'toilet',
      },
      {
        id: 'swimming',
        label: 'Swimming Pools',
        icon: 'swimming',
      },
      {
        id: 'medical',
        label: 'Medical Facilities',
        icon: 'medical',
      },
      {
        id: 'gym',
        label: 'Gymnasiums',
        icon: 'gym',
      },
    ],
  },
  safetyAndSecurity: {
    title: 'Safety & Security',
    sections: [
      {
        id: 'family-personal',
        title: 'Family & Personal safety',
        items: [
          'Child Friendly',
          'Child Safety',
          'Women Travel Safety',
          'LGBTQ Comforts',
          'Disability Friendly',
        ],
      },
      {
        id: 'environment-location',
        title: 'Environment & Location Safety',
        items: [
          'Clean Locations',
          'No Danger Zones',
          'Safe Zones',
          'Low Crime Rates',
          'Very Safe',
        ],
      },
      {
        id: 'security-emergencies',
        title: 'Security & Emergencies',
        items: [
          'Traveler Advisories',
          'Emergency Evacuation',
          'Personal Safety',
          'Local Friendly',
        ],
      },
    ],
  },
};

// Nexgo Experiences
export const NEXGO_EXPERIENCES: ExperienceCard[] = [
  {
    id: '1',
    title:
      'Ranthambore Safari & Wildlife Experience – Tigers, Forts & Lakeside Magic',
    image:
      'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=300&fit=crop&q=80',
    location: 'Ranthambore',
    type: 'activities',
  },
  {
    id: '2',
    title: 'A romantic Paris Getaway - 7 Days / 6 Nights',
    image:
      'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=400&h=300&fit=crop&q=80',
    location: 'Paris',
    type: 'package',
  },
  {
    id: '3',
    title: 'Ride to Leh from Kerala with Royal Enfield',
    image:
      'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=300&fit=crop&q=80',
    location: 'India',
    type: 'riders-corner',
  },
  {
    id: '4',
    title: 'Clear Kayaking at Johannesburg',
    image:
      'https://images.unsplash.com/photo-1501785888041-af3ef285b470?w=400&h=300&fit=crop&q=80',
    location: 'Johannesburg',
    type: 'activities',
  },
  {
    id: '5',
    title: 'Dubai Safari Trails for Family',
    image:
      'https://images.unsplash.com/photo-1511497584788-876760111969?w=400&h=300&fit=crop&q=80',
    location: 'Dubai',
    type: 'signature',
  },
  {
    id: '6',
    title: 'Bike Ride through the Mountains of Tibet',
    image:
      'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=300&fit=crop&q=80',
    location: 'Tibet',
    type: 'package',
  },
];
